import { get_headers, get_project_id } from "@/utils/constants/constants";
import { PDF_SIGNATORY_TYPE_OPTIONS } from "@/utils/pdf_report_signature_helpers";
import {
  FORMAL_TEMPLATE_WIZARD_STEPS,
  build_generation_mode_label,
  build_system_field_options,
} from "./formal_document_template_wizard_helpers";

export const get_default_section_type = (formal_lookups) => {
  const field_list = (formal_lookups?.section_types || []).find(
    (section_type) => section_type.internal_name === "field_list"
  );

  return field_list?.internal_name || "field_list";
};

export const formal_document_template_wizard_data = () => {
  const wizard_state = {
    headers: get_headers(),
    project_id: get_project_id(),
    current_step_key: "basics",
    template_form: null,
    question_options: [],
    loading_questions: false,
    logo_preview_url: "",
    is_dragging_logo: false,
    uploading_logo: false,
    saving_template: false,
    generated_document: null,
    signature_modal_visible: false,
    pending_one_off_generation: null,
    preview_html: "",
    preview_error: "",
    preview_loading: false,
    preview_request_key: 0,
    preview_refresh_timer: null,
    active_section_local_id: "",
    new_section_type: "field_list",
    question_picker_state: {
      modal_title: "Choose linked question",
      section_local_id: "",
      selected_question_ids: [],
      selection_mode: "single",
      target_local_id: "",
      target_type: "",
      visible: false,
    },
    reorder_sections_modal_visible: false,
    current_reorder_sections: [],
  };

  return wizard_state;
};

export const formal_document_template_wizard_computed = {
  wizard_steps() {
    return FORMAL_TEMPLATE_WIZARD_STEPS.map((step) => {
      if (step.key !== "review") {
        return step;
      }

      const step_with_summary = {
        ...step,
        summary: this.generated_document
          ? this.get_generated_document_status_label()
          : step.summary,
      };

      return step_with_summary;
    });
  },

  current_step_index() {
    const current_index = this.wizard_steps.findIndex(
      (step) => step.key === this.current_step_key
    );

    return current_index;
  },

  current_step() {
    const step = this.wizard_steps[this.current_step_index] || this.wizard_steps[0];

    return step;
  },

  entity_type_options() {
    return this.report_lookups?.entity_types || [];
  },

  generation_mode_options() {
    return this.formal_lookups?.generation_modes || [];
  },

  trigger_status_options() {
    return this.formal_lookups?.trigger_statuses || [];
  },

  checklist_field_options() {
    return this.formal_lookups?.checklist_fields || [];
  },

  signatory_type_options() {
    return this.formal_lookups?.signatory_types || PDF_SIGNATORY_TYPE_OPTIONS;
  },

  section_type_options() {
    return this.formal_lookups?.section_types || [];
  },

  system_field_options() {
    return build_system_field_options(this.formal_lookups?.system_fields);
  },

  output_format_options() {
    return this.report_lookups?.output_formats || [];
  },

  is_editing() {
    return Boolean(this.template_form?.template_id);
  },

  active_section() {
    const sections = this.template_form?.sections || [];
    const active_section =
      sections.find(
        (section) => section.local_id === this.active_section_local_id
      ) ||
      sections[0] ||
      null;

    return active_section;
  },

  selected_entity_type_names() {
    const selected_ids = new Set(
      (this.template_form?.entity_type_ids || []).map((item) => Number(item))
    );
    const entity_type_names = this.entity_type_options
      .filter((entity_type) => selected_ids.has(entity_type.id))
      .map((entity_type) => entity_type.entity_type_name);

    return entity_type_names;
  },

  review_generation_label() {
    return build_generation_mode_label(
      this.template_form?.generation_mode,
      this.formal_lookups
    );
  },

  show_preview_panel() {
    return this.current_step_key !== "basics";
  },

  can_toggle_template_save() {
    return this.allow_one_off && !this.is_editing;
  },

  target_entity_profile_id() {
    const parsed = Number(this.initial_target_entity_profile_id || 0);
    const profile_id = Number.isFinite(parsed) && parsed > 0 ? parsed : null;

    return profile_id;
  },

  target_application_id() {
    const parsed = Number(this.initial_application_id || 0);
    const application_id = Number.isFinite(parsed) && parsed > 0 ? parsed : null;

    return application_id;
  },

  target_entity_type_id() {
    const parsed = Number(this.initial_entity_type_id || 0);
    const entity_type_id = Number.isFinite(parsed) && parsed > 0 ? parsed : null;

    return entity_type_id;
  },

  is_profile_scope() {
    return Boolean(this.target_entity_profile_id);
  },

  primary_submit_label() {
    return this.template_form?.save_as_template === false
      ? "Generate PDF"
      : "Save Template";
  },

  generated_document_url() {
    const generated_document_url =
      this.generated_document?.presigned_url ||
      this.generated_document?.file_url ||
      "";

    return generated_document_url;
  },
};

export const formal_document_template_wizard_watch = {
  initial_template: {
    immediate: true,
    handler() {
      this.reset_wizard();
    },
  },

  template_form: {
    deep: true,
    handler(_next_value, previous_value) {
      if (!previous_value || this.saving_template || !this.generated_document) {
        this.schedule_preview_refresh();
        return;
      }

      this.generated_document = null;
      this.schedule_preview_refresh();
    },
  },

  "template_form.save_as_template": {
    handler() {
      if (!this.template_form) {
        return;
      }

      this.handle_template_save_mode_change();
    },
  },
};
